import { useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, Lock, User, Eye, EyeOff, LogIn, UserPlus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { db } from '../firebase'
import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore'
import { useToast } from '../context/ToastContext'

const StaffLogin = () => {
  const { showToast } = useToast()
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  })
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (isLogin) {
        // Login Logic
        const staffRef = collection(db, 'staff')
        const q = query(staffRef, where('email', '==', formData.email), where('password', '==', formData.password))
        const snapshot = await getDocs(q)

        if (snapshot.empty) {
          showToast('Email ose password gabim!', 'error')
          setLoading(false)
          return
        }

        const staffDoc = snapshot.docs[0]
        const staffData = {
          id: staffDoc.id,
          ...staffDoc.data()
        }

        // Save to localStorage
        localStorage.setItem('staffMember', JSON.stringify(staffData))
        
        showToast(`Mirë se erdhe, ${staffData.name}!`, 'success')
        
        // Navigate to dashboard
        navigate('/dashboard-admin')
      } else {
        // Register Logic
        if (!formData.name || !formData.email || !formData.password) {
          showToast('Plotëso të gjitha fushat!', 'warning')
          setLoading(false)
          return
        }

        // Check if email already exists
        const staffRef = collection(db, 'staff')
        const q = query(staffRef, where('email', '==', formData.email))
        const snapshot = await getDocs(q)

        if (!snapshot.empty) {
          showToast('Ky email është regjistruar tashmë!', 'error')
          setLoading(false)
          return
        }

        // Create new staff member
        await addDoc(collection(db, 'staff'), {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          isOnline: false,
          createdAt: serverTimestamp()
        })

        showToast('Llogaria u krijua me sukses! Tani mund të hysh.', 'success')
        setIsLogin(true)
        setFormData({ name: '', email: '', password: '' })
      }
    } catch (error) {
      console.error('Auth error:', error)
      showToast('Gabim gjatë procesit!', 'error')
    }
    
    setLoading(false)
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark via-bronze-900 to-dark flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold text-gradient-gold mb-2">
            Contenta Staff
          </h1>
          <p className="text-beige-300">
            {isLogin ? 'Hyr në llogari' : 'Regjistrohu si anëtar i stafit'}
          </p>
        </div>

        <div className="bg-bronze-900/50 backdrop-blur-xl border border-gold-500/20 rounded-3xl p-8 shadow-2xl">
          <div className="flex space-x-2 mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 rounded-xl font-medium transition-all ${
                isLogin
                  ? 'bg-gradient-gold text-white'
                  : 'text-beige-300 hover:bg-bronze-800/50'
              }`}
            >
              <LogIn className="w-4 h-4 inline mr-2" />
              Hyr
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 rounded-xl font-medium transition-all ${
                !isLogin
                  ? 'bg-gradient-gold text-white'
                  : 'text-beige-300 hover:bg-bronze-800/50'
              }`}
            >
              <UserPlus className="w-4 h-4 inline mr-2" />
              Regjistrohu
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-beige-300 text-sm mb-2">
                  Emri i Plotë *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-beige-400" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    placeholder="Emri Mbiemri"
                    className="w-full pl-11 pr-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-beige-300 text-sm mb-2">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-beige-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="email@contenta.marketing"
                  className="w-full pl-11 pr-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-beige-300 text-sm mb-2">
                Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-beige-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className="w-full pl-11 pr-12 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-beige-400 hover:text-gold-400 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                'Po processohet...'
              ) : isLogin ? (
                <>
                  <LogIn className="w-5 h-5 inline mr-2" />
                  Hyr në Dashboard
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5 inline mr-2" />
                  Krijo Llogari
                </>
              )}
            </button>
          </form>

          {isLogin && (
            <p className="text-center text-beige-400 text-sm mt-4">
              Nuk ke llogari?{' '}
              <button
                onClick={() => setIsLogin(false)}
                className="text-gold-400 hover:text-gold-300 font-medium"
              >
                Regjistrohu këtu
              </button>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default StaffLogin
